/* ============================================================
   Wido Ops — Access control: roles, permission matrix, create role,
   and per-user role assignment.
   Exports window.AdminScreen.
   ============================================================ */
(function () {
  const { useState } = React;
  const D = window.WidoData;
  const USE_API = !!window.WIDO_USE_API;
  const PersonCell = window.HRPersonCell;
  const PERMS = D.PERMISSIONS;
  const GROUPS = [...new Set(PERMS.map((p) => p.group))];

  function CreateRoleDialog({ roles, onClose, onCreate }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [base, setBase] = useState("employee");
    const [accent, setAccent] = useState("#2a7d36");
    const accents = ["#2a7d36", "#3a9a45", "#ef5b2e", "#cf4820", "#aa3411", "#4f6353"];
    return React.createElement("div", { className: "scrim-bg", onClick: onClose },
      React.createElement("div", { className: "sheet", onClick: (e) => e.stopPropagation() },
        React.createElement("div", { className: "sheet__head" },
          React.createElement("div", null,
            React.createElement("div", { className: "eyebrow" }, "Access control"),
            React.createElement("h3", { className: "section-title", style: { fontSize: 22, marginTop: 4 } }, "Create a role")),
          React.createElement("button", { className: "icon-btn", onClick: onClose }, React.createElement("span", { className: "material-symbols-rounded" }, "close"))),
        React.createElement("div", { className: "sheet__body" },
          React.createElement("div", { className: "field" }, React.createElement("label", { className: "field__label" }, "Role name"),
            React.createElement("input", { className: "input", value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g. Accountant, Studio Director" })),
          React.createElement("div", { className: "field" }, React.createElement("label", { className: "field__label" }, "Description"),
            React.createElement("textarea", { className: "input", value: desc, onChange: (e) => setDesc(e.target.value), placeholder: "What can this role do?" })),
          React.createElement("div", { className: "field" }, React.createElement("label", { className: "field__label" }, "Start from"),
            React.createElement("select", { className: "select", value: base, onChange: (e) => setBase(e.target.value) },
              React.createElement("option", { value: "blank" }, "Blank — no permissions"),
              roles.map((r) => React.createElement("option", { key: r.id, value: r.id }, "Clone: " + r.name)))),
          React.createElement("div", { className: "field" }, React.createElement("label", { className: "field__label" }, "Accent"),
            React.createElement("div", { className: "row", style: { gap: 10 } }, accents.map((c) =>
              React.createElement("button", { key: c, onClick: () => setAccent(c), "aria-label": "color",
                style: { width: 32, height: 32, borderRadius: "50%", background: c, border: accent === c ? "3px solid var(--md-on-surface)" : "2px solid var(--md-outline-variant)", cursor: "pointer" } })))),
        ),
        React.createElement("div", { className: "sheet__foot" },
          React.createElement("button", { className: "btn btn--text", onClick: onClose }, "Cancel"),
          React.createElement("button", { className: "btn btn--filled", disabled: !name.trim(),
            onClick: () => { const baseRole = roles.find((r) => r.id === base); onCreate({ id: "custom_" + Date.now(), name: name.trim(), desc: desc || "Custom role.", accent, system: false, perms: base === "blank" ? [] : baseRole.perms.slice() }); } },
            React.createElement("span", { className: "material-symbols-rounded" }, "add"), "Create role"),
        ),
      ),
    );
  }

  function Matrix({ roles, setRoles, onPersistRole }) {
    function toggle(roleId, permId) {
      const role = roles.find((r) => r.id === roleId);
      if (!role || role.system) return;
      const has = role.perms.includes(permId);
      const perms = has ? role.perms.filter((p) => p !== permId) : [...role.perms, permId];
      const updated = { ...role, perms };
      setRoles((rs) => rs.map((r) => (r.id === roleId ? updated : r)));
      if (onPersistRole) onPersistRole(updated);
    }
    const ce = React.createElement;
    // header cells
    const headCells = [ce("th", { key: "h0", className: "perm-row", style: { minWidth: 240 } }, "Permission")];
    roles.forEach((r) => {
      const meta = r.system
        ? ce("span", { className: "muted", style: { fontSize: 10, display: "flex", alignItems: "center", gap: 2 } }, ce("span", { className: "material-symbols-rounded", style: { fontSize: 12 } }, "lock"), "system")
        : ce("span", { className: "muted", style: { fontSize: 10 } }, r.perms.length + " perms");
      headCells.push(ce("th", { key: r.id, style: { minWidth: 92 } },
        ce("div", { className: "stack-v", style: { alignItems: "center", gap: 6 } },
          ce("span", { style: { width: 10, height: 10, borderRadius: "50%", background: r.accent } }),
          ce("span", null, r.name),
          meta)));
    });
    // body rows grouped by permission group
    const bodyRows = [];
    GROUPS.forEach((g) => {
      bodyRows.push(ce("tr", { key: "g-" + g }, ce("td", { className: "grp perm-row", colSpan: roles.length + 1 }, g)));
      PERMS.filter((p) => p.group === g).forEach((p) => {
        const cells = [ce("td", { key: "label", className: "perm-row" },
          ce("div", { className: "stack-v", style: { gap: 2 } },
            ce("span", { style: { font: "500 14px/1.3 var(--font-sans)" } }, p.label),
            ce("span", { className: "muted", style: { fontSize: 12 } }, p.desc)))];
        roles.forEach((r) => {
          const on = r.perms.includes(p.id);
          cells.push(ce("td", { key: r.id },
            ce("button", { className: "check" + (on ? " is-on" : "") + (r.system ? " is-locked" : ""), onClick: () => toggle(r.id, p.id), disabled: r.system, title: r.system ? "System role — permissions are fixed" : (on ? "Remove permission" : "Grant permission"), "aria-label": p.label + " for " + r.name },
              on ? ce("span", { className: "material-symbols-rounded" }, "check") : null)));
        });
        bodyRows.push(ce("tr", { key: p.id }, cells));
      });
    });
    return ce("div", { className: "card tablewrap" },
      ce("table", { className: "matrix" },
        ce("thead", null, ce("tr", null, headCells)),
        ce("tbody", null, bodyRows)));
  }

  function RolesCards({ roles, onNew }) {
    return React.createElement("div", { className: "grid-3" },
      roles.map((r) => React.createElement("div", { key: r.id, className: "card card--pad stack-v", style: { gap: 10, borderTop: "3px solid " + r.accent } },
        React.createElement("div", { className: "between" },
          React.createElement("strong", { style: { fontSize: 16 } }, r.name),
          r.system ? React.createElement("span", { className: "tag tag--neutral", style: { fontSize: 10 } }, React.createElement("span", { className: "material-symbols-rounded" }, "lock"), "System")
            : React.createElement("span", { className: "tag tag--info", style: { fontSize: 10 } }, "Custom")),
        React.createElement("p", { className: "muted", style: { margin: 0, fontSize: 13, lineHeight: 1.45, minHeight: 38 } }, r.desc),
        React.createElement("div", { className: "row", style: { gap: 6, flexWrap: "wrap" } },
          React.createElement("span", { className: "tag tag--neutral mono" }, r.perms.length + " permissions"),
          React.createElement("span", { className: "tag tag--neutral mono" }, D.people.filter((p) => p.roleId === r.id).length + " people")),
      )),
      React.createElement("button", { className: "card card--pad", onClick: onNew,
        style: { border: "1.5px dashed var(--md-outline)", background: "transparent", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--md-primary)", minHeight: 130 } },
        React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 28 } }, "add_circle"),
        React.createElement("span", { style: { font: "500 15px/1 var(--font-sans)" } }, "Create role")),
    );
  }

  function PeopleTab({ roles, onToast, onRefresh }) {
    const [people, setPeople] = useState(D.people.map((p) => ({ ...p })));
    const [q, setQ] = useState("");
    async function setRole(id, roleId) {
      if (USE_API) {
        try { await window.WidoApi.updatePerson(id, { roleId }); onToast && onToast("Role updated"); if (onRefresh) await onRefresh(); }
        catch (e) { onToast && onToast("Couldn't update role"); }
        return;
      }
      setPeople((ps) => ps.map((p) => p.id === id ? { ...p, roleId } : p)); onToast && onToast("Role updated");
    }
    async function toggleStatus(id) {
      const cur = people.find((p) => p.id === id);
      const next = cur && cur.status === "active" ? "deactivated" : "active";
      if (USE_API) {
        try {
          if (next === "deactivated") await window.WidoApi.deactivate(id); else await window.WidoApi.activate(id, cur ? cur.roleId : undefined);
          onToast && onToast(next === "active" ? "Activated" : "Deactivated");
          if (onRefresh) await onRefresh();
        } catch (e) { onToast && onToast("Couldn't update account"); }
        return;
      }
      setPeople((ps) => ps.map((p) => p.id === id ? { ...p, status: p.status === "active" ? "deactivated" : "active" } : p));
    }
    const rows = people.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    const statusTag = (s) => s === "active" ? React.createElement("span", { className: "tag tag--ok" }, React.createElement("span", { className: "dot-status", style: { background: "var(--md-primary)" } }), "Active")
      : s === "pending" ? React.createElement("span", { className: "tag tag--warn" }, "Pending")
      : React.createElement("span", { className: "tag tag--err" }, "Deactivated");
    return React.createElement("div", { className: "stack-v", style: { gap: 16 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("p", { className: "muted", style: { margin: 0 } }, people.filter((p) => p.status === "active").length + " active · " + people.length + " total"),
        React.createElement("input", { className: "input", style: { height: 40, width: 220 }, placeholder: "Search people…", value: q, onChange: (e) => setQ(e.target.value) })),
      React.createElement("div", { className: "card tablewrap" },
        React.createElement("table", { className: "data" },
          React.createElement("thead", null, React.createElement("tr", null, ["Employee", "Team", "Reports to", "Role", "Status", ""].map((h) => React.createElement("th", { key: h }, h)))),
          React.createElement("tbody", null, rows.map((p) => {
            const leader = D.helpers.leaderOf(p);
            return React.createElement("tr", { key: p.id },
              React.createElement("td", null, React.createElement(PersonCell, { person: p })),
              React.createElement("td", { className: "muted" }, window.rawText(D.helpers.teamName(p.team))),
              React.createElement("td", { className: "muted" }, leader ? leader.name : "—"),
              React.createElement("td", null, React.createElement("select", { className: "select", style: { height: 36, fontSize: 13 }, value: p.roleId, onChange: (e) => setRole(p.id, e.target.value) },
                roles.map((r) => React.createElement("option", { key: r.id, value: r.id }, r.name)))),
              React.createElement("td", null, statusTag(p.status)),
              React.createElement("td", null, React.createElement("button", { className: "btn btn--text btn--sm", onClick: () => toggleStatus(p.id), disabled: p.status === "pending" }, p.status === "active" ? "Deactivate" : "Activate")),
            );
          })),
        ),
      ),
    );
  }

  function AdminScreen({ onToast, onRefresh }) {
    const [roles, setRoles] = useState(D.ROLES.map((r) => ({ ...r, perms: r.perms.slice() })));
    const [tab, setTab] = useState("roles");
    const [creating, setCreating] = useState(false);
    function persistRole(role) {
      if (!USE_API) return;
      window.WidoApi.saveRole({ id: role.id, name: role.name, accent: role.accent, desc: role.desc, perms: role.perms })
        .catch(() => onToast && onToast("Couldn't save role"));
    }
    async function createRole(r) {
      setRoles((rs) => [...rs, r]);
      setCreating(false);
      if (USE_API) {
        try { await window.WidoApi.saveRole({ id: r.id, name: r.name, accent: r.accent, desc: r.desc, perms: r.perms }); onToast && onToast("Role “" + r.name + "” created"); if (onRefresh) await onRefresh(); }
        catch (e) { onToast && onToast("Couldn't create role"); }
        return;
      }
      onToast && onToast("Role “" + r.name + "” created");
    }
    return React.createElement("div", { className: "stack-v", style: { gap: 20 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("div", null,
          React.createElement("h2", { className: "section-title" }, "Access control"),
          React.createElement("p", { className: "muted", style: { margin: "4px 0 0" } }, "Roles, permissions and who can see what.")),
        tab === "roles" && React.createElement("button", { className: "btn btn--filled", onClick: () => setCreating(true) },
          React.createElement("span", { className: "material-symbols-rounded" }, "add"), "Create role")),
      React.createElement("div", { className: "tabbar" },
        React.createElement("button", { className: "tabbar__tab" + (tab === "roles" ? " is-on" : ""), onClick: () => setTab("roles") }, "Roles & permissions"),
        React.createElement("button", { className: "tabbar__tab" + (tab === "people" ? " is-on" : ""), onClick: () => setTab("people") }, "People & assignments")),
      tab === "roles" ? React.createElement("div", { className: "stack-v", style: { gap: 20 } },
        React.createElement(RolesCards, { roles, onNew: () => setCreating(true) }),
        React.createElement("div", null,
          React.createElement("div", { className: "between", style: { marginBottom: 12 } },
            React.createElement("h3", { className: "section-title", style: { fontSize: 18 } }, "Permission matrix"),
            React.createElement("span", { className: "muted", style: { fontSize: 13 } }, "Tap a cell to toggle · system roles are locked")),
          React.createElement(Matrix, { roles, setRoles, onPersistRole: persistRole })),
      ) : React.createElement(PeopleTab, { roles, onToast, onRefresh }),
      creating && React.createElement(CreateRoleDialog, { roles, onClose: () => setCreating(false), onCreate: createRole }),
    );
  }

  window.AdminScreen = AdminScreen;
})();
